<!-- Simple DataTable component -->
<template>
  <div class="data-table">
    <div class="table-header">
      <div class="table-controls">
        <input 
          v-if="searchable" 
          v-model="searchQuery" 
          type="text" 
          placeholder="Search table data..." 
          class="search-input"
        >
        <div class="table-actions">
          <span class="table-info">{{ filteredData.length }} rows</span>
        </div>
      </div>
    </div>
    
    <div class="table-container">
      <table class="data-table-table">
        <thead>
          <tr>
            <th 
              v-for="column in columns" 
              :key="column.key"
              @click="sortBy(column.key)"
              :class="{ sortable: column.sortable, sorted: sortColumn === column.key }"
            >
              {{ column.title }}
              <span v-if="column.sortable && sortColumn === column.key" class="sort-indicator">
                {{ sortDirection === 'asc' ? '↑' : '↓' }}
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, index) in paginatedData" :key="row.id || index" @click="selectRow(row)">
            <td v-for="column in columns" :key="column.key">
              <span v-if="column.render" v-html="column.render(row[column.key])" />
              <span v-else>{{ row[column.key] }}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    
    <div v-if="paginate" class="table-footer">
      <div class="pagination">
        <button 
          @click="currentPage = Math.max(1, currentPage - 1)" 
          :disabled="currentPage === 1"
          class="pagination-btn"
        >
          Previous
        </button>
        <span class="pagination-info">
          Page {{ currentPage }} of {{ totalPages }}
        </span>
        <button 
          @click="currentPage = Math.min(totalPages, currentPage + 1)" 
          :disabled="currentPage === totalPages"
          class="pagination-btn"
        >
          Next
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';

interface Column {
  key: string;
  title: string;
  sortable?: boolean;
  filterable?: boolean;
  width?: string;
  render?: (value: any) => string;
  align?: 'left' | 'center' | 'right';
}

interface DataTableProps {
  data: any[];
  columns: Column[];
  searchable?: boolean;
  sortable?: boolean;
  pageSize?: number;
  paginate?: boolean;
  showColumnToggle?: boolean;
  showExport?: boolean;
  selectionMode?: 'single' | 'multiple' | 'none';
}

const props = withDefaults(defineProps<DataTableProps>(), {
  searchable: false,
  sortable: false,
  pageSize: 10,
  paginate: false,
  showColumnToggle: false,
  showExport: false,
  selectionMode: 'none'
});

const emit = defineEmits<{
  'row-select': [rows: any[]];
  'sort': [sortInfo: { column: string; direction: 'asc' | 'desc' }];
  'filter': [filterInfo: { column?: string; value: string }];
  'export': [format: 'csv' | 'json' | 'excel'];
}>();

const searchQuery = ref('');
const sortColumn = ref<string | null>(null);
const sortDirection = ref<'asc' | 'desc'>('asc');
const currentPage = ref(1);
const selectedRows = ref<any[]>([]);

const filteredData = computed(() => {
  if (!searchQuery.value) return props.data;
  return props.data.filter(row => 
    Object.values(row).some(value => 
      String(value).toLowerCase().includes(searchQuery.value.toLowerCase())
    )
  );
});

const sortedData = computed(() => {
  if (!sortColumn.value) return filteredData.value;
  
  return [...filteredData.value].sort((a, b) => {
    const aVal = a[sortColumn.value!];
    const bVal = b[sortColumn.value!];
    
    if (aVal < bVal) return sortDirection.value === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection.value === 'asc' ? 1 : -1;
    return 0;
  });
});

const totalPages = computed(() => {
  if (!props.paginate) return 1;
  return Math.ceil(sortedData.value.length / props.pageSize);
});

const paginatedData = computed(() => {
  if (!props.paginate) return sortedData.value;
  
  const start = (currentPage.value - 1) * props.pageSize;
  return sortedData.value.slice(start, start + props.pageSize);
});

const sortBy = (column: string) => {
  const columnConfig = props.columns.find(col => col.key === column);
  if (!columnConfig?.sortable) return;
  
  if (sortColumn.value === column) {
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortColumn.value = column;
    sortDirection.value = 'asc';
  }
  
  emit('sort', { column, direction: sortDirection.value });
};

const selectRow = (row: any) => {
  if (props.selectionMode === 'none') return;
  
  if (props.selectionMode === 'single') {
    selectedRows.value = [row];
  } else if (props.selectionMode === 'multiple') {
    const index = selectedRows.value.findIndex(r => r.id === row.id);
    if (index >= 0) {
      selectedRows.value.splice(index, 1);
    } else {
      selectedRows.value.push(row);
    }
  }
  
  emit('row-select', selectedRows.value);
};
</script>

<style scoped lang="scss">

.data-table {
  background-color: var(--vscode-background);
  border: 1px solid var(--vscode-border);
  border-radius: var(--border-radius);
  overflow: hidden;
}

.table-header {
  padding: var(--spacing-md);
  border-bottom: 1px solid var(--vscode-border);
  background-color: var(--vscode-backgroundSecondary);
}

.table-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--spacing-md);
}

.search-input {
  padding: var(--spacing-sm);
  border: 1px solid var(--vscode-border);
  border-radius: var(--border-radius);
  background-color: var(--vscode-input-background);
  color: var(--vscode-input-foreground);
  font-family: var(--vscode-font-family);
  min-width: 250px;
  
  &:focus {
    outline: none;
    border-color: var(--vscode-focusBorder);
  }
}

.table-info {
  font-size: 12px;
  color: var(--vscode-descriptionForeground);
}

.table-container {
  max-height: 400px;
  overflow-y: auto;
}

.data-table-table {
  width: 100%;
  border-collapse: collapse;
  font-family: var(--vscode-font-family);
  font-size: var(--vscode-font-size);
  
  th {
    background-color: var(--vscode-backgroundSecondary);
    padding: var(--spacing-sm) var(--spacing-md);
    text-align: left;
    font-weight: 600;
    color: var(--vscode-foreground);
    border-bottom: 1px solid var(--vscode-border);
    position: sticky;
    top: 0;
    
    &.sortable {
      cursor: pointer;
      user-select: none;
      
      &:hover {
        background-color: var(--vscode-list-hoverBackground);
      }
    }
    
    &.sorted {
      background-color: var(--vscode-list-activeSelectionBackground);
    }
  }
  
  td {
    padding: var(--spacing-sm) var(--spacing-md);
    border-bottom: 1px solid var(--vscode-border);
    color: var(--vscode-foreground);
  }
  
  tbody tr {
    &:hover {
      background-color: var(--vscode-list-hoverBackground);
    }
    
    &:nth-child(even) {
      background-color: var(--vscode-list-inactiveSelectionBackground);
    }
  }
}

.sort-indicator {
  margin-left: var(--spacing-xs);
  font-size: 12px;
}

.table-footer {
  padding: var(--spacing-md);
  border-top: 1px solid var(--vscode-border);
  background-color: var(--vscode-backgroundSecondary);
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: var(--spacing-md);
}

.pagination-btn {
  padding: var(--spacing-xs) var(--spacing-md);
  border: 1px solid var(--vscode-border);
  background-color: var(--vscode-button-background);
  color: var(--vscode-button-foreground);
  border-radius: var(--border-radius);
  cursor: pointer;
  font-family: var(--vscode-font-family);
  
  &:hover:not(:disabled) {
    background-color: var(--vscode-button-hoverBackground);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.pagination-info {
  font-size: 12px;
  color: var(--vscode-descriptionForeground);
}
</style>
